import { Subject, firstValueFrom, merge, from } from 'rxjs';

import { cssPrefixes } from '@pressreader/types';

function getProp() {
    const propName = 'hidden';
    const capitalizedPropName = 'Hidden';
    let propFullName = '';

    // Loop over all the known prefixes until we find one
    for (const { fun } of cssPrefixes) {
        if (fun) {
            propFullName = fun + capitalizedPropName;
        } else {
            propFullName = propName;
        }
        if (propFullName in document) {
            return propFullName;
        }
    }

    // otherwise it's not supported
    return null;
}

const prop = getProp();

function isHidden() {
    if (!prop) {
        return false;
    }
    return (document as any)[prop] as boolean;
}

function isVisible() {
    return !isHidden();
}

const subject = new Subject<void>();

if (prop) {
    const evtname = `${prop.replace(/[H|h]idden/, '')}visibilitychange`;
    document.addEventListener(evtname, () => {
        if (!isHidden()) {
            subject.next();
        }
    });
}

function whenVisible() {
    if (!isHidden() || !prop) {
        return Promise.resolve();
    }
    return firstValueFrom(subject);
}

function setInterval(callback: () => void, interval: number, firstDelay = 0) {
    let lastFireTime = Date.now();

    if (firstDelay) {
        lastFireTime -= interval - firstDelay;
    }

    const minDelay = Math.min(interval, 100);
    const timer = new Subject<void>();
    let timeoutId = 0;

    function nextTimeout(timeout: number) {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
        timeoutId = window.setTimeout(() => {
            timeoutId = 0;
            timer.next();
        }, timeout);
    }

    const { unsubscribe } = merge(subject, timer, from([0])).subscribe(() => {
        if (isHidden()) {
            return;
        }
        const timeSinceLastFire = Date.now() - lastFireTime;
        const nextDelay = interval - timeSinceLastFire;
        if (nextDelay > minDelay) {
            nextTimeout(Math.min(nextDelay, interval));
            return;
        }
        callback();
        lastFireTime = Date.now();
        nextTimeout(interval);
    });

    return {
        dispose() {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
            unsubscribe();
        },
    };
}

export { isVisible, whenVisible, setInterval };
